import { Button } from '@qi/ui';
import { createFileRoute } from '@tanstack/react-router';
import { Check, LifeBuoy, Mail } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_marketing/support')({
  component: SupportPage,
});

// Where support requests land. No server mail infra exists yet, so the form
// composes the message and hands off to the visitor's own mail client via a
// mailto: link — it works end-to-end today and can be swapped for an API
// endpoint later without changing the form.
const SUPPORT_EMAIL = 'harmonichealthcenter@gmail.com';

const TOPICS = [
  'Billing & payments',
  'Scan or camera issue',
  'My account',
  'Frequencies & protocols',
  'Something else',
] as const;

function SupportPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState<(typeof TOPICS)[number]>(TOPICS[0]);
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const subject = `Qi Scan support — ${topic}`;
    const body = `${message}\n\n— ${name || 'A Qi Scan user'}${email ? ` (${email})` : ''}`;
    const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
    setSent(true);
  }

  return (
    <div className="mx-auto max-w-xl px-6 py-16">
      <div className="mb-10 flex flex-col items-center gap-4 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full border border-aurora/30 bg-surface-2 text-aurora">
          <LifeBuoy size={22} strokeWidth={1.7} />
        </span>
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">Support</p>
        <h1 className="font-display text-4xl leading-tight text-ink-0">How can we help?</h1>
        <p className="max-w-md text-pretty text-ink-1">
          Tell us what's going on and we'll get back to you. Most questions are answered within a
          day.
        </p>
      </div>

      {sent ? (
        <div
          className="qi-glass flex flex-col items-center gap-3 rounded-lg p-8 text-center"
          aria-live="polite"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-jade/15 text-jade">
            <Check size={20} strokeWidth={2.2} />
          </span>
          <p className="font-display text-xl text-ink-0">Your message is ready to send</p>
          <p className="max-w-sm text-sm text-ink-2">
            We opened your email app with the details filled in — just hit send. If nothing opened,
            email us directly at{' '}
            <a className="text-aurora hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
          <Button variant="ghost" size="md" onClick={() => setSent(false)}>
            Write another message
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <Field id="support-name" label="Your name">
            <input
              id="support-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              className={INPUT_CLASS}
              placeholder="Jane Doe"
            />
          </Field>
          <Field id="support-email" label="Your email">
            <input
              id="support-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              inputMode="email"
              autoComplete="email"
              spellCheck={false}
              className={INPUT_CLASS}
              placeholder="you@somewhere.com"
            />
          </Field>
          <Field id="support-topic" label="Topic">
            <select
              id="support-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value as (typeof TOPICS)[number])}
              className={INPUT_CLASS}
            >
              {TOPICS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field id="support-message" label="Message">
            <textarea
              id="support-message"
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className={`${INPUT_CLASS} resize-y`}
              placeholder="Tell us what's happening…"
            />
          </Field>
          <Button type="submit" variant="resonance" size="lg" className="w-full">
            <Mail size={15} strokeWidth={1.9} aria-hidden="true" />
            Send message
          </Button>
          <p className="text-center text-xs text-ink-3">
            Prefer email? Reach us directly at{' '}
            <a
              className="text-ink-1 hover:text-aurora hover:underline"
              href={`mailto:${SUPPORT_EMAIL}`}
            >
              {SUPPORT_EMAIL}
            </a>
          </p>
        </form>
      )}
    </div>
  );
}

const INPUT_CLASS =
  'w-full rounded-sm border border-hairline bg-surface-3 px-4 py-3 text-sm text-ink-0 placeholder:text-ink-3 focus:border-aurora/60 focus:outline-none focus:ring-4 focus:ring-aurora/15';

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1.5 block font-mono text-xs uppercase tracking-[0.18em] text-ink-2"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
